import type { AdminTransactionPage, TransactionSource } from '@nestjs-boilerplate/types';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import { manyStrings, oneOf, toCsv } from '@/lib/query-params';

export type { AdminTransaction, AdminTransactionPage } from '@nestjs-boilerplate/types';

/**
 * Filters the transaction screen can apply. A field left undefined is not sent,
 * so the server sees no filter rather than an empty one.
 */
export interface TransactionFilters {
  user?: string;
  companyId?: string;
  source?: TransactionSource;
  /** Any of these types matches. Empty or absent means no type filter. */
  type?: string[];
  from?: string;
  to?: string;
  sortBy?: 'occurredAt' | 'amountPence' | 'type';
  sortOrder?: 'asc' | 'desc';
}

export const transactionQueryKeys = {
  all: ['admin', 'transactions'] as const,
  list: (filters: TransactionFilters) => [...transactionQueryKeys.all, filters] as const,
};

/** The columns the API can sort by. */
const TRANSACTION_SORT_COLUMNS: readonly NonNullable<TransactionFilters['sortBy']>[] = [
  'occurredAt',
  'amountPence',
  'type',
];

/** The sort directions the API accepts. */
const SORT_ORDERS: readonly NonNullable<TransactionFilters['sortOrder']>[] = ['asc', 'desc'];

/**
 * Fetches one page of transactions.
 *
 * @param filters - Active filters
 * @param page - 1-based page number
 * @param pageSize - Rows per page
 */
export async function getTransactions(
  filters: TransactionFilters,
  page = 1,
  pageSize = 20,
): Promise<AdminTransactionPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

  for (const [key, value] of Object.entries(filters)) {
    // The ordering pair is allowlisted below rather than copied through: these
    // two come from the URL and reach a raw SQL ORDER BY, so they are the one
    // pair that must not ride the pass-everything loop.
    if (key === 'sortBy' || key === 'sortOrder') {
      continue;
    }
    // `type` is a list. Joined explicitly rather than left to `URLSearchParams`
    // stringifying the array, which would also send `type=` for an empty
    // selection — a cleared multi-select would come back a 400.
    if (Array.isArray(value)) {
      const list = toCsv(manyStrings(value));
      if (list) {
        params.set(key, list);
      }
      continue;
    }
    // Empty strings come from cleared inputs and must not become filters.
    if (value) {
      params.set(key, value);
    }
  }

  // Only sent together: an order with no column would be a direction applied to
  // the server's default, which is not a thing the server offers.
  const sortBy = oneOf(TRANSACTION_SORT_COLUMNS, filters.sortBy);
  if (sortBy) {
    params.set('sortBy', sortBy);
    params.set('sortOrder', oneOf(SORT_ORDERS, filters.sortOrder) ?? 'desc');
  }

  return apiClient.fetch<AdminTransactionPage>(`/admin/transactions?${params.toString()}`);
}

/**
 * Query hook for a page of transactions.
 *
 * `placeholderData` keeps the previous page on screen while the next loads, so
 * paging does not blink through an empty table.
 */
export function useTransactions(filters: TransactionFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...transactionQueryKeys.list(filters), page, pageSize],
    queryFn: () => getTransactions(filters, page, pageSize),
    placeholderData: (previous) => previous,
  });
}
